import { useCallback, useEffect, useState } from "react";
import { Ban, Loader2, Plus, RotateCcw, Search } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { api, formatApiError } from "../../lib/api";
import { Badge, EmptyRow, Table, fmtDate } from "../office/ui";

const STATUS = { disponible: ["activa", "Disponible"], enviado: ["pendiente", "Enviado"], baja: ["rechazado", "Baja"] };

const PlatformCard = ({ p, onGenerated, selected, onSelect }) => {
  const [count, setCount] = useState(10);
  const [busy, setBusy] = useState(false);
  const generate = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/codes/generate", { platform: p.platform, count: Number(count) });
      toast.success(`${data.created} códigos creados: ${data.first} a ${data.last}`);
      onGenerated();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div onClick={() => onSelect(p.platform)} className={`cursor-pointer rounded-2xl border bg-white p-4 transition-colors ${selected ? "border-ink" : "border-ink/10 hover:border-ink/30"}`} data-testid={`codes-platform-${p.platform.replace(" ", "-")}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="font-display text-lg font-semibold">{p.platform}</p>
          <p className="font-mono text-[11px] text-ink/60">Formato {p.example} · siguiente {p.next_code}</p>
        </div>
        <span className="rounded-full bg-paper px-2.5 py-1 font-mono text-xs">{p.total}</span>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs">
        <div className="rounded-xl bg-leaf/15 py-2"><p className="font-display text-lg font-semibold text-leaf-deep">{p.disponibles}</p>disponibles</div>
        <div className="rounded-xl bg-sun/20 py-2"><p className="font-display text-lg font-semibold text-[#6b4f00]">{p.enviados}</p>enviados</div>
        <div className="rounded-xl bg-coral/10 py-2"><p className="font-display text-lg font-semibold text-coral">{p.baja}</p>baja</div>
      </div>
      <div className="mt-3 flex gap-2" onClick={(e) => e.stopPropagation()}>
        <Input type="number" min={1} max={500} value={count} onChange={(e) => setCount(e.target.value)} className="h-9 w-24 bg-white" data-testid={`codes-count-${p.platform.replace(" ", "-")}`} />
        <button onClick={generate} disabled={busy} className="btn-ink !h-9 !px-4 !py-0 !text-xs disabled:opacity-60" data-testid={`codes-generate-${p.platform.replace(" ", "-")}`}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Crear códigos
        </button>
      </div>
    </div>
  );
};

export const CodesInventory = () => {
  const [summary, setSummary] = useState(null);
  const [platform, setPlatform] = useState("CHALAN");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [codes, setCodes] = useState(null);

  const loadSummary = useCallback(() => api.get("/admin/codes/summary").then(({ data }) => setSummary(data)), []);
  const loadCodes = useCallback(() => api.get("/admin/codes", { params: { platform, status, q } }).then(({ data }) => setCodes(data)), [platform, status, q]);
  useEffect(() => {
    loadSummary();
  }, [loadSummary]);
  useEffect(() => {
    const t = setTimeout(loadCodes, 250);
    return () => clearTimeout(t);
  }, [loadCodes]);

  const refresh = () => {
    loadSummary();
    loadCodes();
  };

  const toggle = async (c) => {
    const action = c.status === "baja" ? "reactivate" : "deactivate";
    if (action === "deactivate" && !window.confirm(`¿Dar de baja el código ${c.code}?`)) return;
    try {
      await api.post(`/admin/codes/${c.code}/${action}`);
      toast.success(action === "deactivate" ? "Código dado de baja" : "Código reactivado");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="codes-inventory">
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {!summary && <Loader2 className="h-6 w-6 animate-spin" />}
        {summary?.map((p) => <PlatformCard key={p.platform} p={p} onGenerated={refresh} selected={platform === p.platform} onSelect={setPlatform} />)}
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-2">
        <h3 className="mr-2 font-display text-xl font-semibold">Códigos de {platform}</h3>
        {[["", "Todos"], ["disponible", "Disponibles"], ["enviado", "Enviados"], ["baja", "Baja"]].map(([v, l]) => (
          <button key={v} onClick={() => setStatus(v)} className={`rounded-full px-3.5 py-2 text-xs font-semibold ${status === v ? "bg-ink text-white" : "border border-ink/15 bg-white hover:bg-paper"}`} data-testid={`codes-filter-${v || "all"}`}>{l}</button>
        ))}
        <div className="relative sm:ml-auto">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/50" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Código, ID o nombre" className="h-9 w-64 bg-white pl-9" data-testid="codes-search" />
        </div>
      </div>
      <Table headers={["Código", "Estado", "Enviado a", "Teléfono", "Fecha de envío", "Vence", ""]} testId="codes-table" minWidth="min-w-[860px]">
        {!codes && <tr><td colSpan={7} className="px-4 py-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>}
        {codes && !codes.length && <EmptyRow cols={7} text="No hay códigos en esta vista. Crea códigos con el botón de la plataforma." testId="codes-empty" />}
        {codes?.map((c, i) => (
          <tr key={c.code} className="border-b border-ink/5 last:border-0" data-testid={`code-row-${i}`}>
            <td className="px-4 py-2.5 font-mono text-sm font-semibold">{c.code}</td>
            <td className="px-4 py-2.5"><Badge status={STATUS[c.status][0]} label={STATUS[c.status][1]} /></td>
            <td className="px-4 py-2.5">{c.sent_to ? <>{c.sent_to.name} <span className="font-mono text-xs text-ink/60">({c.sent_to.dist_id})</span></> : "—"}</td>
            <td className="px-4 py-2.5 font-mono text-xs">{c.sent_to?.phone || "—"}</td>
            <td className="px-4 py-2.5 text-ink/70">{fmtDate(c.sent_at)}</td>
            <td className="px-4 py-2.5 text-ink/70">{fmtDate(c.expires_at)}</td>
            <td className="px-4 py-2.5 text-right">
              <button onClick={() => toggle(c)} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${c.status === "baja" ? "border border-ink/15 hover:bg-paper" : "bg-coral/10 text-coral hover:bg-coral/20"}`} data-testid={`code-toggle-${c.code}`}>
                {c.status === "baja" ? <><RotateCcw className="h-3.5 w-3.5" /> Reactivar</> : <><Ban className="h-3.5 w-3.5" /> Dar de baja</>}
              </button>
            </td>
          </tr>
        ))}
      </Table>
    </div>
  );
};
